// src/components/Reports/SupplierProductInsights.js
//
// "Suppliers by Product" - written for a pharmacy owner: who sells me each product, what do I really pay, and who has
// the best price right now. Prices are per unit and include free goods and discounts. "Best recent price" only compares
// suppliers bought from in the last 90 days, so prices are compared in the same time window.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import axiosInstance from "../../services/axiosInstance";
import { handleApiError } from "../apiErrorHandler";
import { localYmd, ymdToDate } from "../../utils/dateOnly";
import useIsMobile from "../../hooks/useIsMobile";

const ENDPOINT = "/api/reports/v2/supplier-product-insights/";
const PAGE_SIZE = 20;
const GREEN = "#10B981";
const AMBER = "#D97706";
const BLUE = "#3B82F6";
const GRAY = "#94A3B8";

const PERIODS = [
    { label: "Last 3 months", value: 90 },
    { label: "Last 6 months", value: 182 },
    { label: "Last 12 months", value: 365 },
];
const FILTERS = [
    { label: "Could save money", value: "saving" },
    { label: "Several suppliers", value: "several" },
    { label: "All products", value: "all" },
];

const money0 = (n) => `Rs. ${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const money2 = (n) => (n === null || n === undefined ? "-" : `Rs. ${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const prettyDate = (s) => {
    const d = ymdToDate(s);
    return d ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-";
};

const Pill = ({ color, children }) => (
    <span
        className="text-xs font-semibold border-round px-2 py-1 mr-1"
        style={{ background: `${color}22`, color, whiteSpace: "nowrap", display: "inline-block" }}
    >
        {children}
    </span>
);

const Tile = ({ label, value, sub, color }) => (
    <div className="surface-card shadow-1 border-round-lg p-3 h-full" style={{ borderTop: `3px solid ${color}` }}>
        <div className="text-xs text-600 mb-1">{label}</div>
        <div className="font-bold text-lg" style={{ color, wordBreak: "break-word" }}>{value}</div>
        {sub && <div className="text-xs text-500 mt-1">{sub}</div>}
    </div>
);

// How one supplier compares with the best one, in plain words.
const compareText = (s) => {
    if (s.is_best) return <span style={{ color: GREEN, fontWeight: 600 }}>Best price</span>;
    if (!s.is_recent) return <span style={{ color: GRAY }}>Old price</span>;
    if (s.vs_best_pct === null || s.vs_best_pct === undefined) return <span style={{ color: GRAY }}>-</span>;
    if (s.vs_best_pct < 1) return <span style={{ color: GRAY }}>About the same</span>;
    return <span style={{ color: AMBER, fontWeight: 600 }}>{s.vs_best_pct}% more</span>;
};

const SupplierBadges = ({ s }) => (
    <>
        {s.is_best && <Pill color={GREEN}>Best price</Pill>}
        {s.is_usual && <Pill color={BLUE}>Your usual supplier</Pill>}
        {!s.is_recent && <Pill color={GRAY}>Old price</Pill>}
    </>
);

const SupplierTable = ({ suppliers, isMobile }) => {
    if (isMobile) {
        return (
            <div>
                {suppliers.map((s) => (
                    <div key={s.supplier} className="border-1 border-200 border-round p-2 mb-2" style={{ background: s.is_best ? `${GREEN}0F` : undefined }}>
                        <div className="font-semibold mb-1">{s.supplier}</div>
                        <div className="mb-2"><SupplierBadges s={s} /></div>
                        <div className="flex flex-wrap gap-3 text-sm">
                            <div><div className="text-500 text-xs">{s.is_recent ? "Recent price" : "Last price"}</div><b>{money2(s.recent_price ?? s.last_price)}</b></div>
                            <div><div className="text-500 text-xs">Compared with best</div>{compareText(s)}</div>
                            <div><div className="text-500 text-xs">Last bought</div>{prettyDate(s.last_bought)}</div>
                            <div><div className="text-500 text-xs">Times bought</div>{s.times_bought}</div>
                            {s.free_pct > 0 && <div><div className="text-500 text-xs">Free goods</div>{s.free_pct}%</div>}
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <table style={{ width: "100%", borderCollapse: "collapse" }} className="text-sm">
            <thead>
                <tr className="text-600 text-left" style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <th className="py-2">Supplier</th>
                    <th>Recent price (per unit)</th>
                    <th>Compared with best</th>
                    <th>Last bought</th>
                    <th>Times bought</th>
                    <th>Free goods</th>
                </tr>
            </thead>
            <tbody>
                {suppliers.map((s) => (
                    <tr key={s.supplier} style={{ borderBottom: "1px solid #f1f5f9", background: s.is_best ? `${GREEN}0F` : undefined }}>
                        <td className="py-2 pr-2">
                            <div className="font-semibold">{s.supplier}</div>
                            <div className="mt-1"><SupplierBadges s={s} /></div>
                        </td>
                        <td><b>{money2(s.recent_price ?? s.last_price)}</b></td>
                        <td>{compareText(s)}</td>
                        <td>{prettyDate(s.last_bought)}</td>
                        <td>{s.times_bought}</td>
                        <td>{s.free_pct > 0 ? `${s.free_pct}%` : "-"}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const ProductCard = ({ p, open, onToggle, isMobile }) => {
    const bestOnce = p.suppliers.find((s) => s.is_best)?.times_bought === 1;
    return (
    <Card className="shadow-1 border-round-lg mb-3">
        <div className="flex justify-content-between align-items-start gap-2 flex-wrap">
            <div style={{ minWidth: 0 }}>
                <div className="font-bold text-900" style={{ wordBreak: "break-word" }}>{p.product}</div>
                {p.code && <div className="text-xs text-500">Code {p.code}</div>}
            </div>
            <div className="flex align-items-center gap-2 flex-wrap">
                <Pill color={BLUE}>{p.supplier_count} supplier{p.supplier_count > 1 ? "s" : ""}</Pill>
                {p.saving_rs > 0 && <Pill color={GREEN}>Could save about {money0(p.saving_rs)}</Pill>}
            </div>
        </div>

        <div className="mt-2 text-sm" style={{ lineHeight: 1.6 }}>
            {p.best_supplier ? (
                <div>
                    <i className="pi pi-check-circle mr-2" style={{ color: GREEN }} />
                    Best recent price: <b>{money2(p.best_price)}</b> from <b>{p.best_supplier}</b>
                    {bestOnce && <span className="text-500 text-xs ml-2">(bought only once - check it is still on offer)</span>}
                </div>
            ) : (
                <div className="text-600">
                    <i className="pi pi-info-circle mr-2" />
                    {p.note || `Only ${p.usual_supplier} supplies this product.`}
                </div>
            )}
            {p.best_supplier && p.usual_vs_best_pct > 0 && (
                <div style={{ color: AMBER }}>
                    <i className="pi pi-exclamation-triangle mr-2" />
                    You usually buy from <b>{p.usual_supplier}</b> at {money2(p.usual_price)}, which is {p.usual_vs_best_pct}% more.
                </div>
            )}
            {p.best_supplier && p.note && <div className="text-600"><i className="pi pi-info-circle mr-2" />{p.note}</div>}
        </div>

        {p.supplier_count > 1 && (
            <>
                <Button
                    label={open ? "Hide suppliers" : "Compare suppliers"}
                    icon={open ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                    iconPos="right"
                    className="p-button-text p-button-sm mt-2 px-0"
                    onClick={onToggle}
                />
                {open && <div className="mt-2"><SupplierTable suppliers={p.suppliers} isMobile={isMobile} /></div>}
            </>
        )}
    </Card>
    );
};

const SupplierProductInsights = () => {
    const isMobile = useIsMobile();
    const [days, setDays] = useState(365);
    const [filter, setFilter] = useState("saving");
    const [searchText, setSearchText] = useState("");
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openIds, setOpenIds] = useState({});
    const [showHelp, setShowHelp] = useState(false);
    const reqId = useRef(0);

    useEffect(() => {                       // wait for the user to stop typing
        const t = setTimeout(() => setSearch(searchText.trim()), 350);
        return () => clearTimeout(t);
    }, [searchText]);

    const params = useCallback((extra = {}) => {
        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - days + 1);
        return { from_date: localYmd(from), to_date: localYmd(to), filter, search, ...extra };
    }, [days, filter, search]);

    const load = useCallback(async (pageNo) => {
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        try {
            const { data } = await axiosInstance.get(ENDPOINT, { params: params({ page: pageNo, page_size: PAGE_SIZE }) });
            if (id !== reqId.current) return;           // a newer request replaced this one
            setSummary(data.summary);
            setTotal(data.total);
            setPage(pageNo);
            setRows((old) => (pageNo === 1 ? data.data : [...old, ...data.data]));
        } catch (err) {
            if (id !== reqId.current) return;
            setError("Could not load the report. Please try again.");
            handleApiError(err);
        } finally {
            if (id === reqId.current) setLoading(false);
        }
    }, [params]);

    useEffect(() => { setOpenIds({}); load(1); }, [load]);

    const exportCsv = async () => {
        try {
            const res = await axiosInstance.get(ENDPOINT, { params: params({ export: "csv" }), responseType: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            link.download = "suppliers-by-product.csv";
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            handleApiError(err);
        }
    };

    const top = summary?.top_suppliers?.[0];
    const share = (n) => Math.max(2, Math.min(100, n));

    return (
        <div>
            {summary && (
                <div className="grid mb-2">
                    <div className="col-6 lg:col-3">
                        <Tile color="#6366F1" label="Products you buy" value={summary.products_bought.toLocaleString()} sub={`from ${summary.suppliers_used} suppliers`} />
                    </div>
                    <div className="col-6 lg:col-3">
                        <Tile color={BLUE} label="Bought from several suppliers" value={summary.several_suppliers.toLocaleString()}
                              sub={summary.products_bought ? `${Math.round((100 * summary.several_suppliers) / summary.products_bought)}% of your products` : ""} />
                    </div>
                    <div className="col-6 lg:col-3">
                        <Tile color={GREEN} label={`You could save (last ${summary.recent_days} days)`} value={money0(summary.possible_saving_rs)}
                              sub={`on ${summary.products_to_review} products, if you had bought each at its best recent price`} />
                    </div>
                    <div className="col-6 lg:col-3">
                        <Tile color={AMBER} label="Your biggest supplier" value={top ? top.supplier : "-"} sub={top ? `${top.share_pct}% of everything you spend` : ""} />
                    </div>
                </div>
            )}

            {summary?.top_suppliers?.length > 0 && (
                <Card className="shadow-1 border-round-lg mb-3">
                    <div className="font-semibold mb-2">Where your money goes</div>
                    {summary.top_suppliers.map((s) => (
                        <div key={s.supplier} className="mb-2">
                            <div className="flex justify-content-between text-sm">
                                <span style={{ minWidth: 0, wordBreak: "break-word" }}>{s.supplier}</span>
                                <span className="text-600 ml-2" style={{ whiteSpace: "nowrap" }}>{s.share_pct}%</span>
                            </div>
                            <div style={{ background: "#E2E8F0", height: 8, borderRadius: 4 }}>
                                <div style={{ width: `${share(s.share_pct)}%`, height: 8, borderRadius: 4, background: BLUE }} />
                            </div>
                        </div>
                    ))}
                </Card>
            )}

            <div className={isMobile ? "flex flex-column gap-2 mb-3" : "flex flex-wrap align-items-center gap-3 mb-3"}>
                {isMobile ? (
                    <Dropdown value={filter} options={FILTERS} onChange={(e) => setFilter(e.value)} className="w-full" />
                ) : (
                    <SelectButton value={filter} options={FILTERS} optionLabel="label" optionValue="value"
                                  onChange={(e) => e.value && setFilter(e.value)} allowEmpty={false} />
                )}
                <Dropdown value={days} options={PERIODS} onChange={(e) => setDays(e.value)} className={isMobile ? "w-full" : undefined} />
                <IconField iconPosition="left" className={isMobile ? "w-full" : undefined}>
                    <InputIcon className="pi pi-search" />
                    <InputText value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search a product"
                               className={isMobile ? "w-full" : undefined} style={isMobile ? undefined : { width: 240 }} />
                </IconField>
                <div className={isMobile ? "flex gap-2" : "flex gap-2 ml-auto"}>
                    <Button label="How to read this" icon="pi pi-question-circle" className="p-button-text p-button-sm" onClick={() => setShowHelp((v) => !v)} />
                    <Button label="Export CSV" icon="pi pi-download" className="p-button-outlined p-button-secondary p-button-sm" onClick={exportCsv} />
                </div>
            </div>

            {showHelp && (
                <Message severity="info" className="w-full mb-3 justify-content-start" content={
                    <ul className="m-0 pl-3 text-sm" style={{ lineHeight: 1.6 }}>
                        <li><b>Price</b> is what you really paid for one unit, including free goods and discounts. The <b>recent price</b> is the average over the last {summary?.recent_days || 90} days, so a supplier who gives free goods on some orders is compared fairly.</li>
                        <li><b>Best recent price</b> compares only suppliers you bought from in the last {summary?.recent_days || 90} days. A supplier you have not bought from lately shows its last price as "Old price".</li>
                        <li><b>Your usual supplier</b> is the one you buy this product from most often.</li>
                        <li>A price from a supplier you bought from <b>only once</b> may not be repeatable, so confirm it before you switch.</li>
                        <li><b>Could save</b> is an estimate: what you paid in the last {summary?.recent_days || 90} days compared with buying all of it at the best recent price.</li>
                        <li>If prices look very different between suppliers, check the pack sizes: one supplier may be priced per pack and another per tablet.</li>
                    </ul>
                } />
            )}

            {error && <Message severity="error" text={error} className="w-full mb-3" />}
            {loading && <ProgressBar mode="indeterminate" style={{ height: 4 }} className="mb-3" />}

            {!loading && !error && rows.length === 0 && (
                <div className="flex flex-column align-items-center py-5 text-600 text-center">
                    <i className="pi pi-check-circle text-4xl mb-3" style={{ color: GREEN }} />
                    {filter === "saving" ? (
                        <>
                            <span>No savings found for this period. Your recent prices look in line across suppliers.</span>
                            <Button label="Show products with several suppliers" className="p-button-text mt-2" onClick={() => setFilter("several")} />
                        </>
                    ) : (
                        <span>No products match.</span>
                    )}
                </div>
            )}

            {rows.map((p) => (
                <ProductCard key={p.product_id} p={p} isMobile={isMobile} open={!!openIds[p.product_id]}
                             onToggle={() => setOpenIds((o) => ({ ...o, [p.product_id]: !o[p.product_id] }))} />
            ))}

            {rows.length > 0 && (
                <div className="flex flex-column align-items-center mb-3">
                    <span className="text-sm text-600 mb-2">Showing {rows.length} of {total.toLocaleString()} products</span>
                    {rows.length < total && (
                        <Button label="Show more" icon="pi pi-angle-down" className="p-button-outlined" disabled={loading} onClick={() => load(page + 1)} />
                    )}
                </div>
            )}
        </div>
    );
};

export default SupplierProductInsights;
